import { CiReport } from '@cibo/core'
import { Loading } from '@cibo/profile'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { DataGridPro } from 'consts/Theme'
import { useInventory } from 'features/Inventories'
import { ascend, mean, pipe, pluck, prop, propEq, reject, sort } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WaitingPage } from './WaitingPage'

interface BasePeriodRow {
  year: number
  soy: number
  corn: number
}

type BaselinePeriodProps = { inventoryId: string }

export const BaselinePeriod = ({ inventoryId }: BaselinePeriodProps) => {
  const { t } = useTranslation('@cibo/Inventories/BaselinePeriod')

  const [refetchInterval, setRefetchInterval] = useState<number | false>(1000)
  const { data: inventory, isLoading, dataUpdatedAt } = useInventory(inventoryId, {
    refetchInterval,
  })

  useEffect(() => {
    inventory?.item.data.ciJobResult?.status === 'complete' && setRefetchInterval(false)
  }, [dataUpdatedAt])

  const hasCorn = !!inventory?.item.data.ciJobResult?.ciReport?.find(a => !!a.corn)
  const hasSoy = !!inventory?.item.data.ciJobResult?.ciReport?.find(a => !!a.soybeans)

  const years = inventory?.item.data.years || []

  const columns = useMemo(() => {
    const cornIfPresent = hasCorn
      ? [
          {
            field: 'corn',
            headerName: t('corn'),
            sortable: false,
            flex: 1,
            renderCell: ({ value }: { value: string }) => t('kgCarbon', { value }),
          },
        ]
      : []
    const soyIfPresent = hasSoy
      ? [
          {
            field: 'soybeans',
            headerName: t('soy'),
            sortable: false,
            flex: 1,
            renderCell: ({ value }: { value: string }) => t('kgCarbon', { value }),
          },
        ]
      : []
    return [
      {
        field: 'year',
        headerName: t('year'),
        sortable: false,
        flex: 1,
      },
      ...cornIfPresent,
      ...soyIfPresent,
    ]
  }, [dataUpdatedAt])

  if (isLoading) return <Loading large />

  const rows = pipe<CiReport, CiReport, CiReport>(
    reject(propEq('year', 2024)),
    sort(ascend(prop('year')))
  )(inventory?.item.data.ciJobResult?.ciReport || [])

  const averages = {
    year: t('baselineAverage'),
    corn: mean(pluck('corn', rows)),
    soybeans: mean(pluck('soybeans', rows)),
  }

  if (inventory?.item.data.ciJobResult?.status === 'complete' && !!rows) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
        <Stack padding={3} spacing={1}>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6">{t('basePeriod')}</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {t('basePeriodSubtitle')}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ backgroundColor: 'table.hilightedRow', width: 16, height: 16 }} />
              <Typography>{t('selectedYears')}</Typography>
            </Stack>
          </Stack>

          <DataGridPro<BasePeriodRow>
            sx={{
              '.MuiDataGrid-row--lastVisible': {
                '.MuiDataGrid-cell': {
                  borderBottom: 'none',
                },
              },
              '.MuiDataGrid-row.hilight': { backgroundColor: 'table.hilightedRow' },
            }}
            disableColumnMenu
            hideFooter
            rows={rows ? [...rows, averages] : []}
            getRowId={i => i.year}
            columns={columns as GridColumns<BasePeriodRow>}
            getRowClassName={(params: { id: GridRowId }) =>
              years.includes(params.id as number) ? 'hilight' : 'normal'
            }
            autoHeight
            disableColumnResize
          />
        </Stack>
      </Paper>
    )
  }
  return <WaitingPage />
}
